import { useNavigation, useRoute } from '@react-navigation/native';
import { navigationLevels } from '@v2/pages/machines/common/constants';
import { MachinesScreen, MetricTypeLink, NowSubScreen } from '@v2/pages/machines/common/enums';
import { LevelSubScreen, NcFileBatchEventType } from '@v2/pages/common/enums';
import { MACHINES_MAIN_SCREEN } from '@v2/pages/machines/navigation/config';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedTimeCounter } from '@reducers/machinesV2';
import { setTimePeriod } from '@reducers/params';
import { useHandleParams } from './useHandleParams';
import { KEYS_TAGS } from '@components/imageHandling/common';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { BatchLevelSubScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { PartDetailsScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { JobDetailsScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { selectMachinesHeaderConfig } from '@reducers/details';
import { pluralTitleRegExp } from '@v2/pages/common/constants/base.constants';
import { selectIsWithERP } from '@reducers/membership';

export type ScreenName = (typeof navigationLevels.context)[keyof typeof navigationLevels.context];

export type MetricType = (typeof navigationLevels.metricTypes)[keyof typeof navigationLevels.metricTypes];

export const useHandleNavigationMachinesHeader = () => {
  const route = useRoute();
  const dispatch = useDispatch();
  const navigation = useNavigation<any>();
  const { id, timePeriod } = useHandleParams();

  const headerConfig = useSelector(selectMachinesHeaderConfig);
  const isWithERP: boolean | null = useSelector(selectIsWithERP);

  const activeScreenName = route.name;

  const activeScreenNameWithoutMachineMainScreen = activeScreenName.replace(MACHINES_MAIN_SCREEN, '');

  const isPerformanceSubScreen = Object.values(MetricTypeLink).includes(activeScreenNameWithoutMachineMainScreen.toLowerCase() as MetricTypeLink);

  const isNowSubScreen = activeScreenNameWithoutMachineMainScreen.includes(MachinesScreen.LIVE);

  const isEvents = activeScreenNameWithoutMachineMainScreen.includes(MachinesScreen.EVENTS);

  const isMachinesEventsScreen = isEvents && !isNowSubScreen;

  const isMachinesScreen = activeScreenName.includes(MACHINES_MAIN_SCREEN) || isNowSubScreen;

  const statisticsScope = isMachinesScreen ? KEYS_TAGS.Machine : KEYS_TAGS.Clamping;

  // const activeLevelScreenName = Object.values(NowSubScreen).find((nowScreen) => activeScreenName.includes(nowScreen));

  // const levelSubScreensList = {
  //   [NowSubScreen.PART]: partLevelSubScreens,
  //   [NowSubScreen.JOB]: jobsLevelSubScreens,
  //   [NowSubScreen.BATCH]: batchLevelSubScreens,
  //   [NowSubScreen.NC_FILE]: ncFileLevelSubScreens,
  //   [NowSubScreen.TOOLS]: toolLevelSubScreens,
  // };

  // const subMenuItems = activeLevelScreenName ? levelSubScreensList[activeLevelScreenName] : null;

  // const activeLevelSubScreenName = subMenuItems ? Object.values(subMenuItems).find((l) => activeScreenName.includes(l)) : null;

  const activePerformanceSubScreenName = Object.values(navigationLevels.metricTypes).find((m) =>
    activeScreenNameWithoutMachineMainScreen.includes(m)
  );

  const activeNcFileBatchEventsSubScreenName = Object.values(NcFileBatchEventType).find((m) => activeScreenNameWithoutMachineMainScreen.includes(m));

  const isBatchesScreen = activeScreenNameWithoutMachineMainScreen === MachinesScreen.HISTORY;

  const isScheduledScreen = activeScreenNameWithoutMachineMainScreen.includes(MachinesScreen.SCHEDULED);

  const isTimeline = activeScreenNameWithoutMachineMainScreen.includes(MachinesScreen.TIMELINE);

  const shouldDisplayAllMachinesButton =
    isNowSubScreen || isPerformanceSubScreen || isBatchesScreen || isScheduledScreen || (isTimeline && !isNowSubScreen);

  const shouldTrackScrollPosition = isBatchesScreen || activeScreenName === NCFileDetailsScreen.BATCH + LevelSubScreen.GENERAL;

  const shouldWorkWithDateTime = isPerformanceSubScreen || isTimeline;

  const isPerformanceUtilizationScreen =
    activePerformanceSubScreenName && activePerformanceSubScreenName === navigationLevels.metricTypes.utilization;

  const enableWholePageVerticalScroll = isPerformanceUtilizationScreen;

  const eventsScreenPrefixes = [
    `${NowSubScreen.BATCH}${LevelSubScreen.TIMES}`,
    `${NowSubScreen.NC_FILE}${LevelSubScreen.TIMES}`,
    `${MACHINES_MAIN_SCREEN}${MachinesScreen.EVENTS}`,
  ];

  const eventsScreensToDisableGlobalScroll = eventsScreenPrefixes.flatMap((prefix) =>
    Object.values(NcFileBatchEventType).map((t) => ({
      screen: `${prefix}${t}`,
      condition: true,
    }))
  );

  const disabledGlobalScrollScreensConfig = [
    { screen: `${MACHINES_MAIN_SCREEN}${MachinesScreen.SCHEDULED}`, condition: true },
    { screen: `${MACHINES_MAIN_SCREEN}${MachinesScreen.EVENTS}`, condition: true },
    { screen: `${MACHINES_MAIN_SCREEN}${MachinesScreen.HISTORY}`, condition: true },
    ...eventsScreensToDisableGlobalScroll,
    { screen: `${MACHINES_MAIN_SCREEN}${MachinesScreen.HISTORY}`, condition: true },
    { screen: `${NCFileDetailsScreen.BATCH}${BatchLevelSubScreen.GENERAL}`, condition: pluralTitleRegExp.test(headerConfig.batch) },
    { screen: `${PartDetailsScreen.BATCH}${BatchLevelSubScreen.GENERAL}`, condition: pluralTitleRegExp.test(headerConfig.batch) },
    { screen: `${JobDetailsScreen.BATCH}${BatchLevelSubScreen.GENERAL}`, condition: pluralTitleRegExp.test(headerConfig.batch) },
    {
      screen: `${MACHINES_MAIN_SCREEN}${navigationLevels.metricTypes.utilization}`,
      condition: true,
    },
  ];

  const screensWithBackgroundSystemOnPhone = [`${MACHINES_MAIN_SCREEN}${MachinesScreen.HISTORY}`];

  const shouldUseSystemBackgroundOnPhone = screensWithBackgroundSystemOnPhone.some((s) => activeScreenName.includes(s));

  const shouldUseGlobalScroll =
    !disabledGlobalScrollScreensConfig.find((i) => i.screen === activeScreenName && i.condition) && !enableWholePageVerticalScroll;

  const globalScrollLoaderMatcher = [MachinesScreen.HISTORY];

  const shouldDisplayGlobalScrollLoader = globalScrollLoaderMatcher.some((screenName) => activeScreenName.includes(screenName));

  // const shouldDisplayTimePeriodMenu = isTimeline && !isNowSubScreen;
  // const shouldDisplayNestedPeriodMenu = isTimeline && !activeScreenNameWithoutMachineMainScreen.includes(LevelScreen.BATCH);
  const shouldDisplayTimePeriodMenu = false; // we don't need TimePeriod sub menu, it will be always day
  const shouldDisplayNestedPeriodMenu = false;

  const shouldDisplayNcFileBatchEventsMenu = isMachinesEventsScreen;

  // const shouldDisplayNcFileBatchEventsSubLevelMenu =
  //   activeLevelSubScreenName === LevelSubScreen.TIMES &&
  //   activeLevelScreenName &&
  //   [NowSubScreen.BATCH, NowSubScreen.NC_FILE].includes(activeLevelScreenName);

  const shouldDisplayNowScreenList =
    isNowSubScreen ||
    isMachinesEventsScreen ||
    isTimeline ||
    isScheduledScreen ||
    activeScreenNameWithoutMachineMainScreen.includes(MachinesScreen.HISTORY);

  // const handleNavigateNowSubScreens = (nowScreen: NowSubScreen) => {
  //   const levelSubScreenName = activeLevelSubScreenName || LevelSubScreen.GENERAL;

  //   const withoutSubScreens = !levelSubScreensList[nowScreen]?.length;

  //   if ((activePerformanceSubScreenName || activeNcFileBatchEventsSubScreenName) && levelSubScreenName && !withoutSubScreens) {
  //     const newScreenName = nowScreen + levelSubScreenName + (activePerformanceSubScreenName || activeNcFileBatchEventsSubScreenName);
  //     navigation?.navigate(MACHINES_MAIN_SCREEN + MachinesScreen.LIVE, {
  //       screen: nowScreen,
  //       params: {
  //         screen: nowScreen + levelSubScreenName,
  //         params: {
  //           screen: newScreenName,
  //           params: {
  //             ...route.params,
  //             id,
  //             timePeriod,
  //           },
  //         },
  //       },
  //     });
  //   } else {
  //     const newScreenName = nowScreen + activeScreenName.slice(activeScreenName.length - levelSubScreenName.length);
  //     navigation?.navigate(MACHINES_MAIN_SCREEN + MachinesScreen.LIVE, {
  //       screen: nowScreen,
  //       params: {
  //         ...(withoutSubScreens
  //           ? {
  //               ...route.params,
  //               id,
  //               timePeriod: undefined,
  //             }
  //           : {
  //               screen: newScreenName,
  //               params: {
  //                 ...route.params,
  //                 id,
  //                 timePeriod: undefined,
  //               },
  //             }),
  //       },
  //     });
  //   }
  // };

  // const handleNavigationLevelSubScreen = (levelSubScreenName: string) => {
  //   if (!isNowSubScreen || !activeLevelScreenName || activeLevelSubScreenName == null) return;

  //   if (levelSubScreenName !== LevelSubScreen.PERFORMANCE) {
  //     const newScreenName = activeLevelScreenName + levelSubScreenName;
  //     navigation?.navigate(MACHINES_MAIN_SCREEN + MachinesScreen.LIVE, {
  //       screen: activeLevelScreenName,
  //       params: { screen: newScreenName, params: { ...route.params, id, timePeriod: undefined } },
  //     });
  //   } else {
  //     const newScreenName = activeLevelScreenName + levelSubScreenName + navigationLevels.metricTypes.active;
  //     navigation?.navigate(MACHINES_MAIN_SCREEN + MachinesScreen.LIVE, {
  //       screen: activeLevelScreenName,
  //       params: {
  //         screen: activeLevelScreenName + levelSubScreenName,
  //         params: { screen: newScreenName, params: { ...route.params, id, timePeriod: undefined } },
  //       },
  //     });
  //   }
  // };

  const handleNavigate = (screenName: ScreenName) => {
    navigation?.navigate(MACHINES_MAIN_SCREEN + screenName, {
      ...route.params,
      id,
      ...(isPerformanceSubScreen ? { timePeriod } : {}),
    });
  };

  const handleNavigateNestedPerformance = (performanceSubScreen: MetricType) => {
    if (!isNowSubScreen || activePerformanceSubScreenName == null) return;
    const newScreenName = activeScreenName.slice(0, activeScreenName.length - activePerformanceSubScreenName.length);
    navigation?.navigate(newScreenName + performanceSubScreen, { ...route.params, timePeriod, id });
  };

  const handleNavigateNcFileBatchEvents = (eventSubScreen: NcFileBatchEventType) => {
    if (!activeNcFileBatchEventsSubScreenName) return;
    const newScreenName = activeScreenName.slice(0, activeScreenName.length - activeNcFileBatchEventsSubScreenName.length);
    navigation?.navigate(newScreenName + eventSubScreen, { ...route.params, id });
  };

  const updateTimePeriod = (timePeriod: string) => {
    dispatch(setSelectedTimeCounter());
    dispatch(setTimePeriod(timePeriod));
  };

  const checkIsLinkSelected = (screenName: ScreenName, isMainMenu: boolean): boolean => {
    switch (screenName) {
      case navigationLevels.context.performance: {
        return isPerformanceSubScreen;
      }
      case navigationLevels.context.events: {
        return isMachinesEventsScreen;
      }
      case navigationLevels.context.live: {
        return isNowSubScreen;
      }
      default: {
        return activeScreenNameWithoutMachineMainScreen.includes(screenName) && (isMainMenu ? !isNowSubScreen : true);
      }
    }
  };

  const shouldUseMachinesForList =
    [MachinesScreen.HISTORY, MachinesScreen.TIMELINE, MachinesScreen.UTILIZATION].some((s) => activeScreenNameWithoutMachineMainScreen.includes(s)) ||
    isNowSubScreen;

  const checkIsMainMenuItemAvailable = (title: unknown): boolean => {
    switch (title) {
      case MachinesScreen.EVENTS:
        return false;
      case MachinesScreen.SCHEDULED:
        return isWithERP === true;
      default:
        return true;
    }
  };

  return {
    handleNavigate,
    updateTimePeriod,
    activeScreenName,
    isPerformanceSubScreen,
    isNowSubScreen,
    isMachinesEventsScreen,
    isTimeline,
    checkIsLinkSelected,
    // handleNavigationLevelSubScreen,
    shouldDisplayNcFileBatchEventsMenu,
    // shouldDisplayNcFileBatchEventsSubLevelMenu,
    // activeLevelSubScreenName,
    activePerformanceSubScreenName,
    activeNcFileBatchEventsSubScreenName,
    // handleNavigateNowSubScreens,
    handleNavigateNestedPerformance,
    handleNavigateNcFileBatchEvents,
    shouldDisplayAllMachinesButton,
    activeScreenNameWithoutMachineMainScreen,
    statisticsScope,
    isMachinesScreen,
    shouldUseGlobalScroll,
    shouldUseMachinesForList,
    shouldDisplayTimePeriodMenu,
    shouldDisplayNestedPeriodMenu,
    // levelSubScreensList,
    // activeLevelScreenName,
    // subMenuItems,
    shouldTrackScrollPosition,
    shouldDisplayGlobalScrollLoader,
    shouldUseSystemBackgroundOnPhone,
    shouldDisplayNowScreenList,
    shouldWorkWithDateTime,
    enableWholePageVerticalScroll,
    checkIsMainMenuItemAvailable,
  };
};
